import { forwardRef, Inject, Injectable }  from '@angular/core';
import { NgbModal }            from '@ng-bootstrap/ng-bootstrap';
import { TranslateService }    from '@ngx-translate/core';

import { API }                              from '../api/api.constant';
import { ApiService }                       from '../api/api.service';
import { AttachmentModel }                  from './models/attachment';
import { AttachmentOptionsModel }           from './models/attachment.options';
import { AttachmentThumbnailStatusModel }   from './models/attachment.thumbnail-status';
import { ConfirmService }                   from '../messages/confirm/confirm.service';
import { ImageLoaderService }               from './attachment.image-loader.service';
import { ImageViewerService }              from '../shared/components/image-viewer/image-viewer.service';
import { JsonService }                      from '../core/services/json.service';
import { MessengerService }                 from '../messages/messenger/messenger.service';
import { SendAttachmentOptionsModel }       from './models/attachment.send-options';
import { SessionService }                   from '../sessions/session.service';
import { VideoViewerComponent }             from './attachment.video-viewer.component';

@Injectable({ providedIn: 'root' })
export class AttachmentsService {
  constructor (
    @Inject(forwardRef(() => ApiService)) private apiService      : ApiService,
    @Inject(forwardRef(() => ConfirmService)) private confirmService  : ConfirmService,
    @Inject(forwardRef(() => ImageViewerService)) private imageViewerApi  : ImageViewerService,
    @Inject(forwardRef(() => JsonService)) private jsonService     : JsonService,
    @Inject(forwardRef(() => MessengerService)) private messengerApi    : MessengerService,
    private modalService    : NgbModal,
    @Inject(forwardRef(() => SessionService))
    private sessionService  : SessionService,
    private translateService: TranslateService,
    @Inject(forwardRef(() => ImageLoaderService)) private imageLoader: ImageLoaderService
  ) {}

  getThumbnails ( thumbnails : AttachmentThumbnailStatusModel, attachments : Array<AttachmentModel> | AttachmentModel ) : Promise<any> {
    attachments = Array.isArray(attachments) ? attachments : [ attachments ];
    attachments = (attachments as Array<AttachmentModel>).filter(attachment => !this.isPdf(attachment));

    thumbnails.isLoading     = true;
    thumbnails.isSuccessful  = false;
    thumbnails.percentLoaded = 0;

    if (!(attachments as Array<AttachmentModel>).length) {
      thumbnails.isLoading    = false;
      thumbnails.isSuccessful = true;

      return Promise.resolve();
    }

    (attachments as Array<AttachmentModel>).forEach(image => {
      image.thumbnail_url = this.getThumbnailIcon(image);
    });

    return this.imageLoader.preloadImages( attachments as Array<AttachmentModel>, 'thumbnail' )
      .then(( loadedAttachments : Array<AttachmentModel> ) => {
        thumbnails.isLoading    = false;
        thumbnails.isSuccessful = true;

        return loadedAttachments;
      })
      .catch(( image : AttachmentModel ) => {
        thumbnails.isLoading    = false;
        thumbnails.isSuccessful = false;

        throw image;
      });
  }

  getThumbnailIcon ( attachment : AttachmentModel ) : string {
    if (this.isVideo(attachment)) {
      return '/assets/images/attachment.video.png';
    }

    if (this.isVehicleVisuals(attachment)) {
      const isCapricornUser = attachment.vehicle_visuals_capricorn_user !== undefined
        ? !!attachment.vehicle_visuals_capricorn_user
        : this.sessionService.company().vehicle_visuals_capricorn_user;

      return `app/vehicle-visuals/img/${ isCapricornUser ? 'vehicle-visuals.capricorn' : 'vehicle-visuals' }.svg`;
    }

    return attachment.thumbnail_url || attachment.attachment_url;
  }

  deleteAttachment ( attachment : AttachmentModel, options : AttachmentOptionsModel ) : Promise<boolean> {
    return this.confirmService.generic(this.translateService.instant('JS_SPACE.CONFIRM.DELETE_ATTACHMENT'))
    .then(() => this.apiService.delete(options.routes.delete, attachment.id));
  }

  getVideoLength ( duration : number ) : string {
    if (!duration) {
      return '0';
    }

    const seconds = new BigDecimal(duration.toString());
    const sixty   = new BigDecimal('60');
    const minutes = seconds.divide(sixty);

    return minutes.setScale(0, BigDecimal.ROUND_UP).toString();
  }

  isImage ( attachment : AttachmentModel ) : boolean {
    return attachment.file_type === 'IMAGE';
  }

  isPdf ( attachment : AttachmentModel ) : boolean {
    return attachment.file_type === 'PDF';
  }

  isVehicleVisuals ( attachment : AttachmentModel ) : boolean {
    return attachment.file_type === 'vehicle visuals url';
  }

  isVideo ( attachment : AttachmentModel | File ) : boolean {
    const type = attachment instanceof File ? attachment.type : attachment.file_type;

    return type.toLowerCase().indexOf('video') >= 0;
  }

  isVideoValid ( video : File ) : boolean {
    const oneMillion      = new BigDecimal('1000000');
    const uploadLimitMb   = new BigDecimal(this.sessionService.company().upload_limit.toString());
    const videoByteSize   = new BigDecimal(video.size.toString());
    const uploadByteLimit = uploadLimitMb.multiply(oneMillion);

    return videoByteSize.isLessThanOrEqualTo(uploadByteLimit);
  }

  sendVideo ( attachment : AttachmentModel, options : SendAttachmentOptionsModel ) : Promise<any> {
    const company = this.sessionService.company();

    if (options.method === 'sms') {
      return this.messengerApi.sms({
        attachment_type : 'video_attachment',
        attachment_table: options.table,
        attachment_id   : attachment.id,
        disclaimer      : 'Note: The link to access the video will be automatically appended to the end of this text when the message is sent.',
        message         : `View your video from ${ company.company_name }`,
        name            : 'Send Video Attachment',
        send_to         : options.contact ? options.contact.phone : ''
      });
    }

    return this.messengerApi.email({
      attachment_id   : attachment.id,
      attachment_table: options.table,
      attachment_type : 'video_attachment',
      body            : 'View video here: %AttachmentURL%',
      name            : 'Send Video Attachment',
      note            : '<span>Note: The link to access the video will be automatically inserted in place of the <strong>%AttachmentURL%</strong> tag</span>',
      email_address   : options.contact ? options.contact.email : '',
      subject         : `View your video from ${ company.company_name }`
    });
  }

  showAttachment ( attachment : AttachmentModel ) : void {
    if (this.isImage(attachment)) {
      this.imageViewerApi.view(attachment.attachment_url);
    }
    else if (this.isVideo(attachment)) {
      const modal = this.modalService.open(VideoViewerComponent, { windowClass: 'video-modal' });

      modal.componentInstance.video = attachment;
    }
    else {
      window.open(attachment.attachment_url, '_blank');
    }
  }

  upload ( file : File, options : AttachmentOptionsModel ) : Promise<any> {
    const fd  = new FormData();
    const xhr = new XMLHttpRequest();

    if (this.isVideo(file) && !this.isVideoValid(file)) {
      return Promise.reject(this.translateService.instant('COMPANY_SPACE.INTEGRATIONS.VIDEO_SETTINGS.VIDEO_IS_TOO_LARGE', {
        size: this.sessionService.company().upload_limit
      }));
    }

    return Promise.resolve()
    .then(() => new Promise<void>(( resolve, reject ) => {
      fd.append(options.id_key, options.entity.id);
      fd.append('description', options.description);
      fd.append('file', file);

      if (this.isVideo(file)) {
        const video = document.createElement('video');

        video.preload = 'metadata';

        video.addEventListener('loadedmetadata', () => {
          window.URL.revokeObjectURL(video.src);

          fd.append('minutes', this.getVideoLength(video.duration));

          resolve();
        });

        video.src = URL.createObjectURL(file);
      }
      else {
        resolve();
      }
    }))
    .then(() => new Promise(( resolve, reject ) => {
      xhr.addEventListener('load', ( e: any ) => {
        if (e.target.status < 400) {
          this.apiService.get(options.routes.get, options.entity.id)
          .then(( attachments : Array<AttachmentModel> ) => resolve(attachments));
        }
        else {
          const error = this.jsonService.parse(e.target.responseText);

          reject(( error && error.message ) ? error.message : error);
        }
      });

      xhr.addEventListener('error', ( e: any ) => {
        reject(e.target.responseText);
      });

      xhr.open('POST', `${ API.url }${ options.routes.create }`, true);
      xhr.withCredentials = true;

      xhr.send(fd);
    }));
  }
}
